import { Pipeline } from '@/api/model/pipeline'
import { PipelinePhaseCategory } from '@/api/types'
import { ReferralStatus } from '@/api/types'

export class ReferralStats {
    age90: number = 0
    age180: number = 0
    age365: number = 0
    ageOther: number = 0
    scorePositive: number = 0
    scoreNeutral: number = 0
    scoreNegative: number = 0
    countsForAllPipelines: number = 0
    countsPerPipeline: Array<{ count: number, potentialOrderValue: number, pipeline: string }> = []
    countsPerPhase: Array<{ count: number, potentialOrderValue: number, phase: string }> = []
    countsPerCategory: Array<{ count: number, potentialOrderValue: number, category: string }> = []
    countsPerStatus: Array<{ count: number, potentialOrderValue: number, status: string }> = []

    private findWithContent(field: string, resultField: string, keys: string[]|null = null): string|null {
        keys = keys || Object.keys(this[field])

        return keys.find(key => {
            for (const entry of this.countsPerCategory) {
                if (entry[resultField] === key && entry.count > 0) {
                    return entry[resultField]
                }
            }
        })
    }

    firstPhaseWithContent(keys: string[]|null = null): string|null {
        return this.findWithContent('countsPerPhase', 'phase', keys)
    }

    firstCategoryWithContent(): string|null {
        return this.findWithContent('countsPerCategory', 'category', [
            PipelinePhaseCategory.ENTRY,
            PipelinePhaseCategory.APPROACH,
            PipelinePhaseCategory.DECISION,
        ])
    }

    firstStatusWithContent(): string|null {
        return this.findWithContent('countsPerStatus', 'status', [
            ReferralStatus.PROCESSED,
            ReferralStatus.SUCCESSFUL,
            ReferralStatus.CLOSED,
        ])
    }

    countForPipeline(pipeline: string|Pipeline|null): number {
        // handle 'alle'
        if (pipeline === null) {
            return this.countsForAllPipelines
        }

        // convert pipeline into its id
        if (pipeline instanceof Pipeline) {
            pipeline = pipeline.id
        }

        return this.countsPerPipeline
            .find(entry => entry.pipeline === pipeline)?.count ?? 0
    }

    potentialOrderValueForPipeline(pipeline: string|Pipeline): number {
        // convert pipeline into its id
        if (pipeline instanceof Pipeline) {
            pipeline = pipeline.id
        }

        return this.countsPerPipeline
            .find(entry => entry.pipeline === pipeline)?.potentialOrderValue ?? 0
    }

    countForPhase(phase: string): number {
        return this.countsPerPhase
            .find(entry => entry.phase === phase)?.count ?? 0
    }

    potentialOrderValueForPhase(phase: string): number {
        return this.countsPerPhase
            .find(entry => entry.phase === phase)?.potentialOrderValue ?? 0
    }

    countForCategory(category: string): number {
        return this.countsPerCategory
            .find(entry => entry.category === category)?.count ?? 0
    }

    potentialOrderValueForCategory(category: string): number {
        return this.countsPerCategory
            .find(entry => entry.category === category)?.potentialOrderValue ?? 0
    }

    countForStatus(status: string): number {
        return this.countsPerStatus
            .find(entry => entry.status === status)?.count ?? 0
    }

    potentialOrderValueForStatus(status: string): number {
        return this.countsPerStatus
            .find(entry => entry.status === status)?.potentialOrderValue ?? 0
    }

    get hasAgeData(): boolean {
        return this.age90 > 0
            || this.age180 > 0
            || this.age365 > 0
            || this.ageOther > 0
    }

    get hasScoreData(): boolean {
        return this.scorePositive > 0
            || this.scoreNeutral > 0
            || this.scoreNegative > 0
    }

    get hasPipelineData(): boolean {
        return this.countsForAllPipelines > 0
            || this.countsPerPipeline.length > 0
            || this.countsPerPhase.length > 0
            || this.countsPerCategory.length > 0
            || this.countsPerStatus.length > 0
    }

    static fromJson(data: any): ReferralStats {
        const instance = new ReferralStats()

        instance.age90 = Number.parseInt(data.age90, 10)
        instance.age180 = Number.parseInt(data.age180, 10)
        instance.age365 = Number.parseInt(data.age365, 10)
        instance.ageOther = Number.parseInt(data.ageOther, 10)
        instance.scorePositive = Number.parseInt(data.scorePositive, 10)
        instance.scoreNeutral = Number.parseInt(data.scoreNeutral, 10)
        instance.scoreNegative = Number.parseInt(data.scoreNegative, 10)
        instance.countsForAllPipelines = Number.parseInt(data.countsForAllPipelines, 10)

        instance.countsPerPipeline = data.countsPerPipeline.map(entry => ({
            pipeline: entry.pipeline,
            count: Number.parseInt(entry.count, 10),
            potentialOrderValue: Number.parseInt(entry.potentialOrderValue, 10),
        }))

        instance.countsPerPhase = data.countsPerPhase.map(entry => ({
            phase: entry.phase,
            count: Number.parseInt(entry.count, 10),
            potentialOrderValue: Number.parseInt(entry.potentialOrderValue, 10),
        }))

        instance.countsPerCategory = data.countsPerCategory.map(entry => ({
            category: entry.category,
            count: Number.parseInt(entry.count, 10),
            potentialOrderValue: Number.parseInt(entry.potentialOrderValue, 10),
        }))

        instance.countsPerStatus = data.countsPerStatus.map(entry => ({
            status: entry.status,
            count: Number.parseInt(entry.count, 10),
            potentialOrderValue: Number.parseInt(entry.potentialOrderValue, 10),
        }))

        return instance
    }
}
